import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Security from "./pages/legal/Security";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthSuccess from "./pages/GoogleAuthSuccess";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import NewProject from "./pages/NewProject";
import ProjectDetail from "./pages/ProjectDetail";
import Team from "./pages/Team";
import AdminOverview from "./pages/AdminOverview";
import CustomerHome from "./pages/CustomerHome";
import CustomerProject from "./pages/CustomerProject";
import ContractorHome from "./pages/ContractorHome";
import ContractorProject from "./pages/ContractorProject";
import SuperAdmin from "./pages/SuperAdmin";
import SuperAdminSettings from "./pages/SuperAdminSettings";
import FirmSettings from "./pages/FirmSettings";
import Contractors from "./pages/Contractors";
import { PlatformProvider } from "./context/PlatformContext";
import { ConfirmProvider } from "./components/ConfirmProvider";
import { Toaster } from "sonner";

function RootRouter() {
  const location = useLocation();
  // Handle Google OAuth callback session_id in URL fragment - synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/security" element={<Security />} />
      <Route path="/google-login" element={<Navigate to="/login" replace />} />
      <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
      <Route path="/change-password" element={
        <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><Dashboard /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><Projects /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/projects/new" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><NewProject /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><ProjectDetail /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><Team /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/admin-overview" element={
        <ProtectedRoute roles={["admin"]}>
          <AppShell><AdminOverview /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/contractors" element={
        <ProtectedRoute roles={["admin", "pm", "designer"]}>
          <AppShell><Contractors /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute roles={["admin"]}>
          <AppShell><FirmSettings /></AppShell>
        </ProtectedRoute>
      } />

      <Route path="/customer" element={
        <ProtectedRoute roles={["customer"]}><CustomerHome /></ProtectedRoute>
      } />
      <Route path="/customer/projects/:projectId" element={
        <ProtectedRoute roles={["customer"]}><CustomerProject /></ProtectedRoute>
      } />

      <Route path="/contractor" element={
        <ProtectedRoute roles={["contractor"]}><ContractorHome /></ProtectedRoute>
      } />
      <Route path="/contractor/projects/:projectId" element={
        <ProtectedRoute roles={["contractor"]}><ContractorProject /></ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute roles={["super_admin", "platform_manager", "platform_staff", "platform_observer", "platform_sales"]}><SuperAdmin /></ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute roles={["super_admin"]}><SuperAdminSettings /></ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PlatformProvider>
          <AuthProvider>
            <ConfirmProvider>
              <RootRouter />
              <Toaster position="top-right" richColors />
            </ConfirmProvider>
          </AuthProvider>
        </PlatformProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
